import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import AnimatedBarChart from "./AnimatedBarChart";
import { memo } from "react";

const Hero = memo(() => {
  return (
    <section
      id="inicio"
      aria-label="Início"
      className="pt-28 md:pt-32 pb-12 md:pb-16 relative overflow-hidden min-h-screen flex items-center bg-white"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-gray-50/40 to-gray-100/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-red-50/30 to-pink-50/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-gray-50/20 to-slate-50/10 rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative max-w-[1400px] w-full">
        {/* Top: text + chart */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
          <motion.div
            className="space-y-6 md:space-y-6 text-center lg:text-left"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, x: -20 },
              visible: {
                opacity: 1,
                x: 0,
                transition: {
                  duration: 0.6,
                  ease: "easeOut",
                  staggerChildren: 0.15,
                  delayChildren: 0.1,
                },
              },
            }}
          >
            <motion.div
              className="flex items-center justify-center lg:justify-start"
              variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
            >
              <div className="w-2 h-2 bg-red-500 rounded-full mr-2 w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-base md:text-sm font-semibold bg-gradient-to-r from-red-600 via-gray-600 to-red-600 bg-clip-text text-transparent">
                Recuperação de crédito ética e eficiente
              </span>
            </motion.div>

            <motion.h1
              className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-extrabold leading-tight"
              variants={{
                hidden: { opacity: 0, y: 18 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
              }}
            >
              <span className="block bg-gradient-to-r from-gray-900 via-red-800 to-gray-800 bg-clip-text text-transparent">
                Estratégia, negociação humanizada
              </span>
              <span className="block text-gray-900 mt-2">
                e segurança jurídica para recuperar valores com consistência.
              </span>
            </motion.h1>

            <motion.p
              className="text-base md:text-base lg:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0 text-gray-600"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { duration: 0.4 } },
              }}
            >
              Somos especialistas em cobranças extrajudiciais e judiciais que
              combinam negociação eficiente, acordos personalizados e profundo
              embasamento jurídico, preservando o relacionamento entre
              empresas/credores e seus consumidores/devedores.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 sm:gap-4 justify-center lg:justify-start mt-8"
              variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
            >
              <Button
                onClick={() => {
                  const contactSection =
                    document.getElementById("fale-conosco");
                  if (contactSection) {
                    contactSection.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }}
                className="text-white px-6 py-4 rounded-lg text-base md:px-6 md:py-3 md:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                style={{
                  background:
                    "linear-gradient(135deg,#F22E3E 0%,#BF2431 50%,#F20505 100%)",
                }}
                aria-label="Fale conosco (rola para a seção de contato)"
              >
                Fale Conosco
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  const targetCards = document.getElementById("target-cards");
                  const consumidoresCard =
                    document.getElementById("consumidores-panel");
                  if (targetCards && consumidoresCard) {
                    targetCards.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                    setTimeout(() => {
                      consumidoresCard.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    }, 500);
                  }
                }}
                className="px-6 py-4 rounded-lg text-base md:px-6 md:py-3 md:text-base font-semibold text-gray-700 border-2 border-gray-200 hover:border-[#F22E3E]/30 hover:text-[#F22E3E] transition-all duration-200"
                aria-label="Nossas soluções (rola para a seção de serviços)"
              >
                <a href="#servicos">Nossas Soluções</a>
              </Button>
            </motion.div>
          </motion.div>

          <motion.div
            className="hidden md:flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.45, duration: 0.45 }}
          >
            {/* chart sem cartão pesado: mais sutil e integrado ao layout */}
            <AnimatedBarChart />
          </motion.div>
        </div>
      </div>
    </section>
  );
});

Hero.displayName = "Hero";

export default Hero;
